<script setup>
import { userAuthState } from "@/@core/auth"
import { MODULES } from '@/@core/utils/helpers'
import logout from '@/services/logout'
import { useTenantStore } from '@/stores/tenant'
import { VNodeRenderer } from "@layouts/components/VNodeRenderer"
import { themeConfig } from "@themeConfig"
import { Vue3Lottie } from 'vue3-lottie'
import { computed } from "vue"
import { useRouter } from 'vue-router'

const tenantStore = useTenantStore()

definePage({
  meta: {
    layout: "blank",
  },
})

const router = useRouter()
const auth = userAuthState()

const computedModules = computed(() => {
  let data = []
  auth?.user?.modules?.forEach(element => {
    data = [
      ...data,
      MODULES.find(x => x.name === element.name),
    ]
  })

  return data.filter(Boolean)
})

const menuItems = [
  {
    title: "Global Settings",
    icon: "tabler-settings",
    action: () => router.push('/app/global/users'),
  },
  {
    title: "Log Out",
    icon: "tabler-logout",
    action: logout,
  },
]

// Get greeting based on time
const greeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good Morning'
  if (hour < 18) return 'Good Afternoon'

  return 'Good Evening'
})

// Get icon based on time of day
const greetingIcon = computed(() => {
  const hour = new Date().getHours()
  if (hour >= 6 && hour < 12) return 'tabler-sunset-2'
  if (hour >= 12 && hour < 18) return 'tabler-sun-high'
  if (hour >= 18 && hour < 21) return 'tabler-sunset-2'

  return 'tabler-moon-stars'
})

// Check if modules count is odd
const hasOddModules = computed(() => computedModules.value.length % 2 !== 0)

// App version
const appVersion = __APP_VERSION__
</script>

<template>
  <div class="panel-wrapper">
    <!-- Background Triangle (hidden on mobile) -->
    <div class="background-triangle d-none d-md-block">
      <img
        src="/assets/panel-page/background-v2.svg"
        alt=""
        class="background-svg"
      >
    </div>

    <VContainer
      fluid
      class="panel-container"
    >
      <!-- Header -->
      <div class="header-section d-flex align-center justify-space-between py-4 py-md-6 px-2 px-md-4">
        <img
          v-if="tenantStore.logo"
          :src="tenantStore.logo"
          alt="Logo"
          class="logo"
          style="object-fit: contain;"
        >
        <VNodeRenderer
          v-else
          :nodes="themeConfig.app.client.logo"
          class="logo"
        />

        <!-- Desktop Menu -->
        <div class="d-none d-sm-flex align-center gap-2">
          <VBtn
            v-for="(item, index) in menuItems"
            :key="item.title"
            variant="text"
            color="default"
            class="header-btn"
            @click="item.action"
          >
            <VIcon
              size="20"
              :icon="item.icon"
              class="me-2"
            />
            {{ item.title }}
          </VBtn>
        </div>

        <!-- Mobile Menu -->
        <div class="d-sm-none">
          <VMenu>
            <template #activator="{ props }">
              <VBtn
                icon="tabler-dots-vertical"
                variant="text"
                v-bind="props"
              />
            </template>
            <VList>
              <VListItem
                v-for="item in menuItems"
                :key="item.title"
                @click="item.action"
              >
                <template #prepend>
                  <VIcon
                    :icon="item.icon"
                    size="20"
                  />
                </template>
                <VListItemTitle>{{ item.title }}</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content">
        <VRow no-gutter>
          <!-- Left Section: Greeting & Modules -->
          <VCol
            cols="12"
            md="7"
            lg="6"
            class="left-section"
          >
            <!-- Welcome Section -->
            <div class="welcome-section mb-6 mb-md-8 px-2 px-md-4">
              <h1 class="text-h4 text-md-h3 font-weight-bold text-high-emphasis mb-2 d-flex align-center gap-2">
                <VIcon
                  :icon="greetingIcon"
                  size="28"
                  class="greeting-icon"
                />
                {{ greeting }}, {{ auth.user?.name || 'Admin' }}!
              </h1>
              <p class="text-body-1 text-medium-emphasis mb-0">
                Welcome to {{ themeConfig.app.title }}. Select a module below to get started.
              </p>
            </div>

            <!-- Modules Grid -->
            <div
              v-if="computedModules.length > 0"
              class="modules-grid px-2 px-md-4"
            >
              <RouterLink
                v-for="module in computedModules"
                :key="module.name"
                :to="module.to"
                class="module-link"
              >
                <div class="module-card">
                  <div class="module-icon-wrapper">
                    <VIcon
                      :icon="module.icon"
                      size="32"
                      class="module-icon"
                    />
                  </div>
                  <h4 class="text-subtitle-1 font-weight-bold text-high-emphasis mt-3 mb-1">
                    {{ module.name }}
                  </h4>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    {{ module.desc }}
                  </p>
                </div>
              </RouterLink>

              <!-- Empty placeholder for odd number of modules -->
              <div
                v-if="hasOddModules"
                class="module-placeholder"
              />
            </div>

            <!-- No Modules Message -->
            <div
              v-else
              class="no-modules-card px-2 px-md-4"
            >
              <VCard
                variant="flat"
                class="border text-center pa-8"
              >
                <VIcon
                  icon="tabler-folder-off"
                  size="64"
                  color="warning"
                  class="mb-4"
                />
                <h3 class="text-h5 mb-2">
                  No Modules Available
                </h3>
                <p class="text-body-2 text-medium-emphasis">
                  You don't have access to any modules yet. Please contact your administrator.
                </p>
              </VCard>
            </div>
          </VCol>

          <!-- Right Section: Illustration (hidden on mobile) -->
          <VCol
            cols="12"
            md="5"
            lg="6"
            class="d-none d-md-flex align-center justify-end right-section"
          >
            <Vue3Lottie
              animation-link="/assets/panel-page/animation.json"
              :loop="true"
              :autoplay="true"
              class="illustration"
            />
          </VCol>
        </VRow>
      </div>

      <!-- Footer -->
      <div class="footer-section px-2 px-md-4 py-4">
        <p class="text-body-2 text-medium-emphasis mb-0">
          © {{ new Date().getFullYear() }} All rights reserved
          <span class="mx-1">|</span>
          <span class="font-weight-medium">WeCareXM</span> v{{ appVersion }}
        </p>
      </div>
    </VContainer>
  </div>
</template>

<style scoped>
.panel-wrapper {
  position: relative;
  overflow: hidden;
  /* background-color: rgb(var(--v-theme-surface)); */
  background-color: #f5f8ff;
  min-block-size: 100vh;
}

.background-triangle {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  block-size: 100%;
  inline-size: auto;
  pointer-events: none;
  z-index: 0;
}

.background-svg {
  block-size: 100%;
  inline-size: auto;
  min-block-size: 100vh;
  object-fit: cover;
  object-position: right top;
}

.panel-container {
  position: relative;
  margin-inline: auto;
  max-inline-size: 1600px;
  z-index: 1;
}

.header-section {
  position: relative;
  z-index: 10;
}

.logo {
  max-inline-size: 180px;
  max-block-size: 60px;
  object-fit: contain;
}

.header-btn {
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
}

.main-content {
  padding-block: 2rem;
  padding-inline: 0.5rem;
}

.welcome-section h1 {
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.2;
}

.modules-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, 1fr);
  max-inline-size: 520px;
}

.module-link {
  display: block;
  text-decoration: none;
}

/* Empty placeholder for odd number of modules (invisible) */
.module-placeholder {
  border: none;
  background: transparent;
}

.module-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  block-size: 100%;
  cursor: pointer;
  min-block-size: 160px;
  text-align: center;
  transition: all 0.2s ease;
}

.module-card:hover {
  border-color: #47B87B;
  background: rgba(71, 184, 123, 0.04);
}

.module-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(71, 184, 123, 0.1);
  block-size: 56px;
  inline-size: 56px;
}

.module-icon {
  color: #47B87B;
}

.greeting-icon {
  color: #f59e0b;
}

.right-section {
  position: relative;
  z-index: 5;
}

.illustration {
  max-block-size: 70vh;
  max-inline-size: 100%;
  object-fit: contain;
}

.footer-section {
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 0;
  inline-size: 100%;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 959px) {
  .logo {
    max-inline-size: 140px;
  }

  .main-content {
    padding-block: 1rem;
  }

  .modules-grid {
    gap: 0.75rem;
    max-inline-size: 100%;
  }

  .module-card {
    padding: 1.25rem 1rem;
  }

  .footer-section {
    position: static;
    margin-block-start: 1rem;
  }
}

@media (max-width: 599px) {
  .logo {
    max-inline-size: 120px;
  }

  .modules-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .module-card {
    padding: 1rem 0.75rem;
    min-block-size: 140px;
  }

  .module-icon-wrapper {
    block-size: 48px;
    inline-size: 48px;
  }

  .module-icon-wrapper .module-icon {
    block-size: 24px !important;
    inline-size: 24px !important;
  }

  .module-card h4 {
    font-size: 0.875rem;
  }

  .module-card p {
    font-size: 0.75rem;
  }

  .greeting-icon {
    block-size: 24px !important;
    inline-size: 24px !important;
  }
}

/* Animation for module cards */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.module-card {
  animation: fadeInUp 0.4s ease-out;
  animation-fill-mode: both;
}

.module-link:nth-child(1) .module-card { animation-delay: 0.05s; }
.module-link:nth-child(2) .module-card { animation-delay: 0.1s; }
.module-link:nth-child(3) .module-card { animation-delay: 0.15s; }
.module-link:nth-child(4) .module-card { animation-delay: 0.2s; }
.module-link:nth-child(5) .module-card { animation-delay: 0.25s; }
.module-link:nth-child(6) .module-card { animation-delay: 0.3s; }
</style>
